import { Box, Button } from "@mui/joy";
import { useCallback, useEffect, useRef, useState } from "react";
import { ProgramContentType } from "data/BlockComponent";
import { setProgramContentData } from "logic/logics";
import { sendMessage } from "utils/webview";
import useGetEventFromApp from "hooks/useGetEventFromApp";
import AudioProgressBar from "./AudioProgressBar";
import { theme } from "styles/theme";
import { PauseIcon, PlayIcon } from "./AudioButtonStatus";
import { useRecoilState } from "recoil";
import { activeAudioPlayerAtom } from "recoil/activeAudioPlayerAtom";
import _ from "lodash";
import { isPlaybackAtEnd } from "./audioReplayLogic";

type PlayerStatus =
  | "idle"
  | "loading"
  | "playing"
  | "buffering"
  | "paused"
  | "finished"
  | "error";

export default function AudioButtonWebview({
  audioInstanceKey,
  audioUrl,
  initialTime,
  setData,
  blockIndex,
  lineIndex,
  cellIndex,
  isCoach,
  title,
  artist,
  artwork,
  extraAudioUrl,
  readonly,
  canReplay,
}: {
  audioInstanceKey: string;
  audioUrl: string;
  initialTime: number;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  blockIndex: number;
  lineIndex: number;
  cellIndex: number;
  isCoach: boolean;
  title?: string;
  artist?: string;
  artwork?: string;
  extraAudioUrl?: string;
  readonly: boolean;
  canReplay?: boolean;
}) {
  const [playerStatus, setPlayerStatus] = useState<PlayerStatus>("idle");
  const [currentTime, setCurrentTime] = useState<number>(initialTime);
  const [duration, setDuration] = useState<number>(0);
  const [isSeeking, setIsSeeking] = useState(false);

  const [activeAudioKey, setActiveAudioKey] = useRecoilState(
    activeAudioPlayerAtom
  );
  const isActive = activeAudioKey === audioInstanceKey;
  const isPlaying = isActive && playerStatus === "playing";
  const showAsPlaying =
    isPlaying ||
    (isActive && playerStatus === "buffering") ||
    (isActive && playerStatus === "loading");
  // 전환 임계값 여유로직(EPS) 적용: 부동소수점/통신 지연으로 인한 조기/지연 전환 흔들림 완화
  const EPS = 0.25;
  const isLive =
    !!extraAudioUrl &&
    duration > 0 &&
    currentTime >= Math.max(0, duration - EPS);

  const showReplay =
    !!canReplay && !showAsPlaying && isPlaybackAtEnd(duration, currentTime);

  useEffect(() => {
    if (isPlaying) {
      return;
    }

    setCurrentTime(initialTime);
  }, [initialTime, isPlaying]);

  useEffect(() => {
    if (!audioUrl || duration > 0) {
      return;
    }

    const audio = new Audio(audioUrl);
    audio.preload = "metadata";

    const handleMetadataLoaded = () => {
      if (isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleError = () => {
      console.error("오디오 메타데이터를 불러오는 데 실패했습니다.", audioUrl);
    };

    // 이벤트 리스너 등록
    audio.addEventListener("loadedmetadata", handleMetadataLoaded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("loadedmetadata", handleMetadataLoaded);
      audio.removeEventListener("error", handleError);
      // audio 객체에 대한 참조를 정리하여 메모리 누수 방지
      audio.src = "";
    };
  }, [audioUrl, duration]);

  const requestLoadAndPlay = useCallback(
    (startTime?: number) => {
      sendMessage(
        JSON.stringify({
          event: "audioAction",
          params: {
            action: "loadAndPlay",
            src: audioUrl,
            initialTime: startTime ?? currentTime,
            audioInstanceKey,
            title,
            artist,
            artwork,
            extraAudioUrl: extraAudioUrl,
          },
        })
      );
    },
    [
      audioUrl,
      currentTime,
      playerStatus,
      audioInstanceKey,
      title,
      artist,
      artwork,
      extraAudioUrl,
    ]
  );

  const requestPause = useCallback(() => {
    sendMessage(
      JSON.stringify({
        event: "audioAction",
        params: { action: "pause", audioInstanceKey },
      })
    );
  }, [audioInstanceKey]);

  const requestResume = useCallback(() => {
    sendMessage(
      JSON.stringify({
        event: "audioAction",
        params: {
          action: "resume",
          src: audioUrl,
          initialTime: currentTime,
          audioInstanceKey,
          title,
          artist,
          artwork,
          extraAudioUrl,
        },
      })
    );
  }, [
    audioUrl,
    currentTime,
    playerStatus,
    audioInstanceKey,
    title,
    artist,
    artwork,
    extraAudioUrl,
  ]);

  const requestSeek = useCallback(
    (seekTime: number) => {
      sendMessage(
        JSON.stringify({
          event: "audioAction",
          params: { action: "seek", audioInstanceKey, seekTime },
        })
      );
    },
    [audioInstanceKey]
  );

  const parsedMessageFromApp = useGetEventFromApp();
  useEffect(() => {
    if (
      parsedMessageFromApp?.event !== "audioPlayer" ||
      parsedMessageFromApp.params?.audioInstanceKey !== audioInstanceKey
    ) {
      return;
    }
    const {
      status: statusFromApp,
      currentTime: timeFromApp,
      duration: durationFromApp,
      seekCompleted,
    } = parsedMessageFromApp.params;

    const newStatus = (
      statusFromApp === "stopped" ? "paused" : statusFromApp
    ) as PlayerStatus;
    setPlayerStatus(newStatus);

    if (seekCompleted) {
      setIsSeeking(false);
    }

    if (!isSeeking) {
      if (typeof timeFromApp === "number" && timeFromApp > 0) {
        setCurrentTime(timeFromApp);
      }
    }

    if (typeof durationFromApp === "number" && durationFromApp > 0) {
      setDuration(durationFromApp);
    }

    if (newStatus === "finished") {
      setActiveAudioKey(null);

      if (!isCoach) {
        setProgramContentData({
          setData,
          blockIndex,
          lineIndex,
          cellIndex,
          newlyAddedData: {
            currentTime,
            ...(currentTime > 0 && {
              id: "meditation_time",
              value: `${Math.floor((currentTime + 10 || 60) / 60)}`,
            }),
            isEnded: true,
          },
        });
      }
    }
  }, [
    parsedMessageFromApp,
    audioInstanceKey,
    isSeeking,
    setActiveAudioKey,
    isCoach,
    setData,
    blockIndex,
    lineIndex,
    cellIndex,
    currentTime,
    duration,
  ]);

  useEffect(() => {
    const isTimeToSave =
      isActive &&
      playerStatus === "playing" &&
      Math.floor(currentTime) % 5 === 0;

    if (isTimeToSave) {
      const isEnded =
        duration > 0 && currentTime > 0 && duration - 30 <= currentTime;
      setProgramContentData({
        setData,
        blockIndex,
        lineIndex,
        cellIndex,
        newlyAddedData: {
          currentTime,
          ...(currentTime > 0 && {
            id: "meditation_time",
            value: `${Math.floor((currentTime + 10 || 60) / 60)}`,
          }),
          ...(isEnded && { isEnded: true }),
        },
      });
    }
  }, [
    isActive,
    playerStatus,
    currentTime,
    duration,
    setData,
    blockIndex,
    lineIndex,
    cellIndex,
  ]);

  useEffect(() => {
    return () => {
      if (activeAudioKey === audioInstanceKey) {
        sendMessage(
          JSON.stringify({
            event: "audioAction",
            params: { action: "gracefulStop", audioInstanceKey },
          })
        );
        setActiveAudioKey(null);
      }
    };
  }, [activeAudioKey, audioInstanceKey, setActiveAudioKey]);

  // 빠른 연속 토글 방지(디바운스)
  const lastToggleRef = useRef<number>(0);
  const canToggle = useCallback(() => {
    const now = Date.now();
    if (now - lastToggleRef.current < 250) return false;
    lastToggleRef.current = now;
    return true;
  }, []);

  const start = useCallback(() => {
    if (!canToggle()) return;
    setActiveAudioKey(audioInstanceKey);
    if (playerStatus === "paused") {
      requestResume();
    } else {
      requestLoadAndPlay();
    }
  }, [
    setActiveAudioKey,
    audioInstanceKey,
    playerStatus,
    requestLoadAndPlay,
    canToggle,
  ]);

  // 다시주기 상태에서 끝까지 들은 오디오를 처음부터 다시 재생
  const replay = useCallback(() => {
    if (!canToggle()) return;
    setCurrentTime(0);
    if (!isCoach) {
      // initialTime prop을 0으로 선기록해 비재생 동기화 effect의 스냅백 방지.
      // merge 방식이라 isEnded·meditation_time은 보존됨 (완료 게이팅 불변).
      setProgramContentData({
        setData,
        blockIndex,
        lineIndex,
        cellIndex,
        newlyAddedData: { currentTime: 0 },
      });
    }
    setActiveAudioKey(audioInstanceKey);
    requestLoadAndPlay(0);
  }, [
    canToggle,
    isCoach,
    setData,
    blockIndex,
    lineIndex,
    cellIndex,
    setActiveAudioKey,
    audioInstanceKey,
    requestLoadAndPlay,
  ]);

  const stop = useCallback(() => {
    if (!canToggle()) return;
    requestPause();
  }, [requestPause, canToggle]);

  const handleSeekStart = () => setIsSeeking(true);
  const handleSeekChange = (newTime: number) => setCurrentTime(newTime);
  const handleSeekEnd = (newTime: number) => {
    requestSeek(newTime);
  };

  const setAudioEnded = useCallback(() => {
    setData((data) => {
      if (data) {
        const data_temp = _.cloneDeep(data);
        const currentContent =
          data_temp[blockIndex].lines[lineIndex][cellIndex].content || {};

        data_temp[blockIndex].lines[lineIndex][cellIndex].content = {
          ...currentContent,
          isEnded: true,
        };
        if (!_.isEqual(data, data_temp)) {
          return data_temp;
        }
        return data;
      }
      return data;
    });
  }, [setData, blockIndex, lineIndex, cellIndex]);

  const handleVisibilityChange = useCallback(() => {
    // 안드로이드 WebView: 백그라운드 전환은 종료로 간주하지 않음
    // (iOS 대응과 동일하게 종료 표시를 제거하여 오동작/전환 경합 방지)
  }, []);

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  return (
    <Box>
      <AudioProgressBar
        currentTime={currentTime}
        duration={duration}
        onSeekStart={handleSeekStart}
        onSeekChange={handleSeekChange}
        onSeekEnd={handleSeekEnd}
        isPlaying={showAsPlaying}
        isLive={isLive}
      />
      <Button
        variant={showAsPlaying ? "Outlined" : "Solid"}
        color="primary"
        fullWidth
        onClick={() =>
          showAsPlaying ? stop() : showReplay ? replay() : start()
        }
        size={"lg"}
        sx={{
          fontSize: "16px",
          fontWeight: 700,
          justifyContent: "center",
          "& .MuiButton-startDecorator": {
            marginRight: 0,
            color: readonly
              ? theme.vars.palette.Content.SuperWeak
              : showAsPlaying
                ? theme.vars.palette.Content.Highlight
                : "white",
          },
          gap: 1,
        }}
        disabled={readonly}
        startDecorator={showAsPlaying ? <PauseIcon /> : <PlayIcon />}
      >
        {showAsPlaying ? "일시정지" : showReplay ? "다시 재생하기" : "재생하기"}
      </Button>
    </Box>
  );
}
